/**
 * Currency utility for handling monetary values with precision
 * 
 * All monetary values are represented as decimal numbers (not cents).
 * Database decimal(10,2) values are parsed as numbers and used directly.
 * All arithmetic uses fixed-point math to avoid floating-point errors.
 * 
 * Example:
 *   $123.45 in database = "123.45" (string) → 123.45 (number) → "$123.45" (display)
 */

/**
 * Parse a price value to a number, ensuring it's a valid decimal
 * Accepts strings, numbers, null, or undefined
 * @param value - Price value from database or API
 * @returns Decimal number with 2 decimal places, or 0 if invalid
 * 
 * Examples:
 *   "123.45" → 123.45
 *   "123.456" → 123.46 (rounds to 2 decimals)
 *   123.45 → 123.45
 *   null → 0
 *   undefined → 0
 */
export function parsePrice(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(num)) {
    return 0;
  }

  // Round to 2 decimal places to match database precision
  return Math.round(num * 100) / 100;
}

/**
 * Format a price for database storage
 * @param amount - Decimal number
 * @returns String like "123.45" suitable for decimal(10,2) columns
 * 
 * Examples:
 *   123.45 → "123.45"
 *   123.456 → "123.46"
 *   0 → "0.00"
 */
export function formatPriceForDb(amount: number): string {
  if (!Number.isFinite(amount)) {
    return "0.00";
  }
  
  return parsePrice(amount).toFixed(2);
}

/**
 * Format a decimal amount as a currency string for display
 * @param amount - Decimal number (e.g., 123.45)
 * @param includeCurrencySymbol - Whether to include "$" symbol (default true)
 * @returns Formatted string like "$123.45"
 * 
 * Examples:
 *   123.45 → "$123.45"
 *   0.01 → "$0.01"
 *   650 → "$650.00"
 *   1234.5 → "$1,234.50"
 *   123.45, false → "123.45"
 */
export function formatMoney(amount: number, includeCurrencySymbol: boolean = true): string {
  const parsed = parsePrice(amount);
  
  const formatted = parsed.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return includeCurrencySymbol ? `$${formatted}` : formatted;
}

/**
 * Apply a percentage to a decimal amount
 * Uses fixed-point math for precision
 * 
 * @param amount - Base amount as decimal number (e.g., 100.00)
 * @param percentValue - Percentage as a number (e.g., 5.25 for 5.25%)
 * @param roundingMode - "up" | "down" | "half-up" (default "half-up")
 * @returns Decimal amount after applying percentage
 * 
 * Examples:
 *   applyPercent(100.00, 5.25) → 5.25 (5.25% of $100.00 = $5.25)
 *   applyPercent(123.45, 30.5) → 37.65 (30.5% of $123.45 = $37.65)
 *   applyPercent(1.00, 33.33) → 0.33 (33.33% of $1.00 = $0.33)
 */
export function applyPercent(
  amount: number,
  percentValue: number,
  roundingMode: "up" | "down" | "half-up" = "half-up"
): number {
  if (!Number.isFinite(amount) || !Number.isFinite(percentValue)) {
    return 0;
  }

  // Use fixed-point math: convert to cents, multiply, divide, convert back
  const amountCents = Math.round(amount * 100);
  const percentCents = Math.round(percentValue * 100);
  const rawResultCents = (amountCents * percentCents) / 10000;

  // Apply rounding based on mode
  let resultCents: number;
  switch (roundingMode) {
    case "up":
      resultCents = Math.ceil(rawResultCents);
      break;
    case "down":
      resultCents = Math.floor(rawResultCents);
      break;
    case "half-up":
    default:
      resultCents = Math.round(rawResultCents);
  }

  // Convert back to decimal
  return parsePrice(resultCents / 100);
}

/**
 * Add two monetary amounts using fixed-point math
 * @param a - First amount as decimal
 * @param b - Second amount as decimal
 * @returns Sum as decimal number
 */
export function addMoney(a: number, b: number): number {
  // Use fixed-point math to avoid floating-point errors
  const aCents = Math.round(parsePrice(a) * 100);
  const bCents = Math.round(parsePrice(b) * 100);
  return parsePrice((aCents + bCents) / 100);
}

/**
 * Subtract monetary amounts using fixed-point math
 * @param a - Amount to subtract from (decimal)
 * @param b - Amount to subtract (decimal)
 * @returns Difference as decimal number
 */
export function subtractMoney(a: number, b: number): number {
  // Use fixed-point math to avoid floating-point errors
  const aCents = Math.round(parsePrice(a) * 100);
  const bCents = Math.round(parsePrice(b) * 100);
  return parsePrice((aCents - bCents) / 100);
}

/**
 * Multiply a monetary amount by a quantity using fixed-point math
 * @param amount - Unit price as decimal (e.g., 3.33)
 * @param quantity - Quantity (can be fractional)
 * @param roundingMode - "up" | "down" | "half-up" (default "half-up")
 * @returns Total as decimal number
 * 
 * Examples:
 *   multiplyMoney(1.00, 3) → 3.00 ($1.00 * 3 = $3.00)
 *   multiplyMoney(3.33, 3) → 9.99 ($3.33 * 3 = $9.99)
 *   multiplyMoney(650.00, 2) → 1300.00 ($650.00 * 2 = $1,300.00)
 */
export function multiplyMoney(
  amount: number,
  quantity: number,
  roundingMode: "up" | "down" | "half-up" = "half-up"
): number {
  if (!Number.isFinite(amount) || !Number.isFinite(quantity)) {
    return 0;
  }

  // Use fixed-point math: convert to cents, multiply, round, convert back
  const amountCents = Math.round(parsePrice(amount) * 100);
  const rawResultCents = amountCents * quantity;

  let resultCents: number;
  switch (roundingMode) {
    case "up":
      resultCents = Math.ceil(rawResultCents);
      break;
    case "down":
      resultCents = Math.floor(rawResultCents);
      break;
    case "half-up":
    default:
      resultCents = Math.round(rawResultCents);
  }

  return parsePrice(resultCents / 100);
}

/**
 * Calculate the maximum of multiple monetary amounts
 * @param amounts - Array of decimal amounts
 * @returns Maximum amount as decimal
 */
export function maxMoney(...amounts: number[]): number {
  const parsed = amounts.map(parsePrice);
  return Math.max(...parsed);
}

/**
 * Calculate the minimum of multiple monetary amounts  
 * @param amounts - Array of decimal amounts
 * @returns Minimum amount as decimal
 */
export function minMoney(...amounts: number[]): number {
  const parsed = amounts.map(parsePrice);
  return Math.min(...parsed);
}

/**
 * Compute a selling price as a whole-number percentage of an MSRP / retail
 * price. Returns null when MSRP is missing, empty, or zero so callers can show
 * a neutral placeholder instead of a broken "Infinity%" / "NaN%" value.
 *
 * @param sellingPrice - Our selling price (decimal string or number)
 * @param msrp - The retail / MSRP value (decimal string or number)
 * @returns Rounded percentage (e.g. 10 for "$10 on a $100 MSRP"), or null
 *
 * Examples:
 *   percentOfMsrp("10", "100") → 10
 *   percentOfMsrp("2023.80", "8095.00") → 25
 *   percentOfMsrp("10", null) → null
 *   percentOfMsrp("10", "0") → null
 */
export function percentOfMsrp(
  sellingPrice: string | number | null | undefined,
  msrp: string | number | null | undefined,
): number | null {
  const retail = parsePrice(msrp);
  if (!Number.isFinite(retail) || retail <= 0) {
    return null;
  }
  const selling = parsePrice(sellingPrice);
  return Math.round((selling / retail) * 100);
}

/**
 * Sum an array of monetary amounts using fixed-point math
 * @param amounts - Array of decimal amounts
 * @returns Total sum as decimal number
 */
export function sumMoney(amounts: number[]): number {
  // Use fixed-point math: convert all to cents, sum, convert back
  const totalCents = amounts.reduce((sum, amount) => {
    return sum + Math.round(parsePrice(amount) * 100);
  }, 0);
  
  return parsePrice(totalCents / 100);
}
